import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate }        from 'react-router-dom';
import { useTranslation }     from 'react-i18next';
import Layout                 from '../../../../components/Layout';
import { useCurrencyFormat }  from '../../../../hooks/useCurrency';
import { salesInvoiceService, SalesInvoiceListDTO, SalesInvoiceStatus } from '../../../../services/salesInvoiceService';
import { salesReportService, SalesDashboardSummaryDTO } from '../../../../services/salesReportService';
import { customerService } from '../../../../services/customerService';
import {
  DollarSign, BarChart2, AlertTriangle, CheckCircle2,
  Receipt, Users, TrendingUp, RefreshCw,
  ShoppingBag, CreditCard, UtensilsCrossed,
  CirclePlus,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import './SalesDashboardPage.css';

// ─── Formatters ───────────────────────────────────────────────────────────────

const fmtNum = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });

const num = (n: number) => fmtNum.format(n);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fmtDate = (iso: string) => {
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

const fmtAxis = (v: number): string => {
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + 'M';
  if (v >= 1_000)     return (v / 1_000).toFixed(0) + 'K';
  return String(v);
};

const extractList = <T,>(d: unknown): T[] => {
  if (Array.isArray(d)) return d as T[];
  if (d && typeof d === 'object') {
    const obj = d as Record<string, unknown>;
    if (Array.isArray(obj['data'])) return obj['data'] as T[];
  }
  return [];
};

const extractTotal = (d: unknown): number => {
  if (d && typeof d === 'object') {
    const obj = d as Record<string, unknown>;
    if (obj['pagination'] && typeof obj['pagination'] === 'object') {
      const p = obj['pagination'] as Record<string, unknown>;
      if (typeof p['total'] === 'number') return p['total'];
    }
  }
  if (Array.isArray(d)) return d.length;
  return 0;
};

// ─── Badge colours ────────────────────────────────────────────────────────────

const INVOICE_STATUS_CLS: Record<SalesInvoiceStatus, string> = {
  Draft:         'ims-badge ims-badge-draft',
  Issued:        'ims-badge ims-badge-confirmed',
  PartiallyPaid: 'ims-badge ims-badge-pending',
  Paid:          'ims-badge ims-badge-approved',
  Overdue:       'ims-badge ims-badge-rejected',
  Voided:        'ims-badge ims-badge-closed',
  Cancelled:     'ims-badge ims-badge-closed',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

interface KpiCardProps {
  icon:       React.ReactNode;
  label:      string;
  value:      string | null;
  sub?:       string;
  accent?:    'green' | 'red' | 'amber' | 'blue' | 'indigo';
  onClick?:   () => void;
}

const KpiCard: React.FC<KpiCardProps> = ({ icon, label, value, sub, accent = 'green', onClick }) => (
  <div
    className={`ims-stat-card${onClick ? ' sd-kpi--link' : ''}`}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter') onClick(); } : undefined}
    style={{ cursor: onClick ? 'pointer' : 'default' }}
  >
    <div className={`ims-stat-icon sd-kpi-icon--${accent}`}>{icon}</div>
    <div className="ims-stat-info">
      {value === null
        ? <span className="sd-skeleton sd-skeleton--val" style={{ display: 'block' }} />
        : <span className="ims-stat-value">{value}</span>
      }
      <span className="ims-stat-label">{label}</span>
      {sub && <div className="sd-kpi-sub">{sub}</div>}
    </div>
  </div>
);

interface StatusBarProps {
  label:    string;
  count:    number;
  total:    number;
  cls:      string;
  amount?:  number;
  currency?: (n: number) => string;
}

const StatusBar: React.FC<StatusBarProps> = ({ label, count, total, cls, amount, currency }) => {
  const pct = total > 0 ? Math.max(2, (count / total) * 100) : 0;
  return (
    <div className="sd-bar-row">
      <div className="sd-bar-meta">
        <span className="sd-bar-label">{label}</span>
        <span className="sd-bar-count">{num(count)}{amount !== undefined && currency ? ` · ${currency(amount)}` : ''}</span>
      </div>
      <div className="sd-bar-track">
        <div className={`sd-bar-fill ${cls}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const SalesDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const currency = useCurrencyFormat();
  const { t }    = useTranslation();

  // ── Raw data ────────────────────────────────────────────────────────────────
  const [invoices,       setInvoices]       = useState<SalesInvoiceListDTO[]>([]);
  const [totalCustomers, setTotalCustomers] = useState<number | null>(null);
  const [dashboardSummary, setDashboardSummary] = useState<SalesDashboardSummaryDTO | null>(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState<string | null>(null);
  const [lastUpdated,    setLastUpdated]    = useState<Date | null>(null);

  // ── Load ────────────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [invRes, custRes, dashRes] = await Promise.allSettled([
        salesInvoiceService.getSalesInvoices({ pageNumber: 1, pageSize: 500 }),
        customerService.getCustomers({ pageNumber: 1, pageSize: 1 }),
        salesReportService.getDashboard(),
      ]);

      if (invRes.status === 'fulfilled') {
        const d = (invRes.value.data as any)?.data;
        setInvoices(extractList<SalesInvoiceListDTO>(d));
      }
      if (custRes.status === 'fulfilled') {
        const d = (custRes.value.data as any)?.data;
        setTotalCustomers(extractTotal(d));
      }
      if (dashRes.status === 'fulfilled') {
        const d = (dashRes.value.data as any)?.data ?? dashRes.value.data;
        setDashboardSummary(d ?? null);
      }

      setLastUpdated(new Date());
    } catch {
      setError(t('salesDashboard.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Derived invoice stats ──────────────────────────────────────────────────
  const invStats = useMemo(() => {
    const byStatus = (s: SalesInvoiceStatus) => invoices.filter(i => i.status === s);

    const paid          = byStatus('Paid');
    const issued        = byStatus('Issued');
    const partiallyPaid = byStatus('PartiallyPaid');
    const overdue       = byStatus('Overdue');
    const draft         = byStatus('Draft');
    const voided        = [...byStatus('Voided'), ...byStatus('Cancelled')];

    const totalRevenue    = paid.reduce((s, i) => s + i.totalAmount, 0);
    const outstanding     = invoices.reduce((s, i) => s + i.balanceDue, 0);
    const overdueAmt      = overdue.reduce((s, i) => s + i.balanceDue, 0);
    const activeRevenue   = [...issued, ...partiallyPaid].reduce((s, i) => s + i.totalAmount, 0);

    // ── Monthly revenue trend (last 6 calendar months) ────────────────────
    const monthMap = new Map<string, { billed: number; paid: number; restaurant: number }>();
    invoices.forEach(inv => {
      const key   = inv.invoiceDate.slice(0, 7); // 'YYYY-MM'
      const entry = monthMap.get(key) ?? { billed: 0, paid: 0, restaurant: 0 };
      entry.billed += inv.totalAmount;
      if (inv.status === 'Paid') entry.paid += inv.totalAmount;
      monthMap.set(key, entry);
    });
    (dashboardSummary?.monthlyTrend ?? []).forEach(pt => {
      const entry = monthMap.get(pt.month) ?? { billed: 0, paid: 0, restaurant: 0 };
      entry.restaurant += pt.restaurantRevenue;
      monthMap.set(pt.month, entry);
    });
    const monthlyData = Array.from(monthMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([key, d]) => ({
        month:      new Date(key + '-01').toLocaleDateString(undefined, { month: 'short', year: '2-digit' }),
        Billed:     Math.round(d.billed),
        Paid:       Math.round(d.paid),
        Restaurant: Math.round(d.restaurant),
      }));

    // ── Invoice status donut slice data ───────────────────────────────────
    const statusPieData = [
      { name: 'Draft',         value: draft.length,         color: '#9ca3af' },
      { name: 'Issued',        value: issued.length,        color: '#3b82f6' },
      { name: 'PartiallyPaid', value: partiallyPaid.length, color: '#f59e0b' },
      { name: 'Paid',          value: paid.length,          color: '#10b981' },
      { name: 'Overdue',       value: overdue.length,       color: '#ef4444' },
      { name: 'Voided',        value: voided.length,        color: '#d1d5db' },
    ].filter(d => d.value > 0);

    return {
      total: invoices.length,
      totalRevenue,
      outstanding,
      overdueAmt,
      activeRevenue,
      counts: {
        Draft:         draft.length,
        Issued:        issued.length,
        PartiallyPaid: partiallyPaid.length,
        Paid:          paid.length,
        Overdue:       overdue.length,
        Voided:        voided.length,
      },
      amounts: {
        Draft:         draft.reduce((s, i) => s + i.totalAmount, 0),
        Issued:        issued.reduce((s, i) => s + i.totalAmount, 0),
        PartiallyPaid: partiallyPaid.reduce((s, i) => s + i.totalAmount, 0),
        Paid:          totalRevenue,
        Overdue:       overdue.reduce((s, i) => s + i.totalAmount, 0),
        Voided:        voided.reduce((s, i) => s + i.totalAmount, 0),
      },
      paymentCounts: {
        Unpaid:        invoices.filter(i => i.paymentStatus === 'Unpaid').length,
        PartiallyPaid: invoices.filter(i => i.paymentStatus === 'PartiallyPaid').length,
        Paid:          invoices.filter(i => i.paymentStatus === 'Paid').length,
      },
      recent: [...invoices].sort((a, b) =>
        new Date(b.invoiceDate).getTime() - new Date(a.invoiceDate).getTime()
      ).slice(0, 5),
      monthlyData,
      statusPieData,
    };
  }, [invoices, dashboardSummary]);

  // ── Revenue collection rate ───────────────────────────────────────────────
  const collectionRate = invStats.total > 0
    ? Math.round((invStats.counts.Paid / invStats.total) * 100)
    : null;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="sd-page">

        {/* ── Hero header ──────────────────────────────────────────────── */}
        <div className="sd-hero">
          <div>
            <h1 className="sd-hero-title"><ShoppingBag size={22} strokeWidth={1.8} style={{verticalAlign:'middle', marginInlineEnd:8}} />{t('salesDashboard.title')}</h1>
            <p className="sd-hero-sub">{t('salesDashboard.subtitle')}</p>
          </div>
          <div className="sd-hero-actions">
            {lastUpdated && (
              <span className="sd-last-updated">
                {t('salesDashboard.lastUpdated')}: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <button className="btn-refresh" onClick={loadData} disabled={loading}>
              <RefreshCw size={14} className={loading ? 'sd-spin' : ''} strokeWidth={2} /> {t('salesDashboard.refresh')}
            </button>
            <button className="sd-btn-primary" onClick={() => navigate('/sales/sales-invoices/create')}>
              <CirclePlus  size={18} strokeWidth={1.75}/>
              {t('salesDashboard.newInvoice')}
            </button>
          </div>
        </div>

        {/* ── Error ────────────────────────────────────────────────────── */}
        {error && <div className="ims-alert ims-alert-error">{error}</div>}

        {/* ── KPI grid — row 1: 4 primary, row 2: 3 secondary (last spans 2) ── */}
        <div className="sd-kpi-grid">
          {/* Row 1 */}
          <KpiCard
            icon={<DollarSign size={28} strokeWidth={1.5} />} label={t('salesDashboard.totalRevenue')}
            value={loading ? null : currency(invStats.totalRevenue)}
            sub={`${num(invStats.counts.Paid)} ${t('salesDashboard.paidInvoices')}`}
            accent="green"
            onClick={() => navigate('/sales/sales-invoices')}
          />
          <KpiCard
            icon={<BarChart2 size={28} strokeWidth={1.5} />} label={t('salesDashboard.outstanding')}
            value={loading ? null : currency(invStats.outstanding)}
            sub={`${num(invStats.counts.Issued + invStats.counts.PartiallyPaid)} ${t('salesDashboard.activeInvoices')}`}
            accent="blue"
            onClick={() => navigate('/sales/sales-invoices')}
          />
          <KpiCard
            icon={<AlertTriangle size={28} strokeWidth={1.5} />} label={t('salesDashboard.overdueBalance')}
            value={loading ? null : currency(invStats.overdueAmt)}
            sub={`${num(invStats.counts.Overdue)} ${t('salesDashboard.overdueInvoices')}`}
            accent="red"
            onClick={() => navigate('/sales/sales-invoices')}
          />
          <KpiCard
            icon={<CheckCircle2 size={28} strokeWidth={1.5} />} label={t('salesDashboard.collectionRate')}
            value={loading ? null : (collectionRate !== null ? `${collectionRate}%` : '—')}
            sub={`${num(invStats.counts.Paid)} / ${num(invStats.total)} ${t('salesDashboard.invoices')}`}
            accent="green"
          />
          {/* Row 2 — 5th and 6th are 1 col, 7th spans 2 to balance */}
          <KpiCard
            icon={<Receipt size={28} strokeWidth={1.5} />} label={t('salesDashboard.totalInvoices')}
            value={loading ? null : num(invStats.total)}
            accent="indigo"
            onClick={() => navigate('/sales/sales-invoices')}
          />
          <KpiCard
            icon={<UtensilsCrossed size={28} strokeWidth={1.5} />} label={t('salesDashboard.combinedRevenue', 'Combined Revenue (6mo)')}
            value={loading ? null : (dashboardSummary ? currency(dashboardSummary.combinedRevenue) : '—')}
            sub={dashboardSummary
              ? `${num(dashboardSummary.invoiceCount)} ${t('salesDashboard.invoices')} + ${num(dashboardSummary.restaurantOrderCount)} ${t('salesDashboard.restaurantOrders', 'restaurant orders')}`
              : undefined}
            accent="amber"
          />
          <KpiCard
            icon={<Users size={28} strokeWidth={1.5} />} label={t('salesDashboard.totalCustomers')}
            value={loading ? null : (totalCustomers !== null ? num(totalCustomers) : '—')}
            accent="blue"
            onClick={() => navigate('/customers')}
          />
          <KpiCard
            icon={<TrendingUp size={28} strokeWidth={1.5} />} label={t('salesDashboard.pipelineValue')}
            value={loading ? null : currency(invStats.activeRevenue)}
            sub={t('salesDashboard.pipelineHint')}
            accent="amber"
          />
        </div>

        {/* ── Charts row ───────────────────────────────────────────────── */}
        <div className="sd-charts-grid">

          {/* Revenue trend — area chart */}
          <div className="ims-card">
            <div className="ims-card-header">
              <span className="ims-card-title">
                <TrendingUp size={15} strokeWidth={2} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                {t('salesDashboard.revenueTrend')}
              </span>
              <span className="sd-chart-hint">{t('salesDashboard.last6Months')}</span>
            </div>
            <div className="ims-card-body sd-chart-body">
              {invStats.monthlyData.length === 0 && !loading ? (
                <p className="sd-empty">{t('salesDashboard.noData')}</p>
              ) : (
                <ResponsiveContainer width="100%" height={230}>
                  <AreaChart data={invStats.monthlyData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="sd-grad-billed" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.18} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="sd-grad-paid" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#10b981" stopOpacity={0.22} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="sd-grad-restaurant" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#f59e0b" stopOpacity={0.22} />
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 11, fill: '#9ca3af' }}
                      axisLine={false} tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#9ca3af' }}
                      tickFormatter={fmtAxis}
                      axisLine={false} tickLine={false}
                      width={52}
                    />
                    <Tooltip
                      formatter={(v: any) => currency(v as number)}
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb', boxShadow: '0 4px 12px rgba(0,0,0,.08)' }}
                    />
                    <Legend iconSize={10} iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                    <Area
                      type="monotone" dataKey="Billed"
                      name={t('salesDashboard.chartBilled')}
                      stroke="#3b82f6" fill="url(#sd-grad-billed)"
                      strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }}
                    />
                    <Area
                      type="monotone" dataKey="Paid"
                      name={t('salesDashboard.chartCollected')}
                      stroke="#10b981" fill="url(#sd-grad-paid)"
                      strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }}
                    />
                    <Area
                      type="monotone" dataKey="Restaurant"
                      name={t('salesDashboard.chartRestaurant', 'Restaurant')}
                      stroke="#f59e0b" fill="url(#sd-grad-restaurant)"
                      strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Invoice status donut */}
          <div className="ims-card">
            <div className="ims-card-header">
              <span className="ims-card-title">
                <Receipt size={15} strokeWidth={2} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                {t('salesDashboard.invoiceDistribution')}
              </span>
            </div>
            <div className="ims-card-body sd-chart-body">
              {invStats.statusPieData.length === 0 && !loading ? (
                <p className="sd-empty">{t('salesDashboard.noData')}</p>
              ) : (
                <ResponsiveContainer width="100%" height={230}>
                  <PieChart>
                    <Pie
                      data={invStats.statusPieData}
                      cx="42%"
                      cy="50%"
                      innerRadius={58}
                      outerRadius={88}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                    >
                      {invStats.statusPieData.map(entry => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v: any, name: string) => [num(v as number), t(`salesDashboard.status${name}`)]}
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb', boxShadow: '0 4px 12px rgba(0,0,0,.08)' }}
                    />
                    <Legend
                      layout="vertical"
                      align="right"
                      verticalAlign="middle"
                      iconSize={10}
                      iconType="circle"
                      formatter={(value: string) => t(`salesDashboard.status${value}`)}
                      wrapperStyle={{ fontSize: 11, paddingInlineStart: 8 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

        </div>

        {/* ── Breakdown + Payment rings ─────────────────────────────────── */}
        <div className="sd-mid-grid">

          {/* Invoice status breakdown */}
          <div className="ims-card">
            <div className="ims-card-header">
              <span className="ims-card-title"><Receipt size={15} strokeWidth={2} style={{verticalAlign:'middle', marginInlineEnd:6}} />{t('salesDashboard.invoiceBreakdown')}</span>
              <button className="sd-link-btn" onClick={() => navigate('/sales/sales-invoices')}>
                {t('salesDashboard.viewAll')} →
              </button>
            </div>
            <div className="ims-card-body">
              {([
                ['Draft',         t('salesDashboard.statusDraft'),         'sd-fill-draft'],
                ['Issued',        t('salesDashboard.statusIssued'),        'sd-fill-issued'],
                ['PartiallyPaid', t('salesDashboard.statusPartiallyPaid'), 'sd-fill-partial'],
                ['Paid',          t('salesDashboard.statusPaid'),          'sd-fill-paid'],
                ['Overdue',       t('salesDashboard.statusOverdue'),       'sd-fill-overdue'],
                ['Voided',        t('salesDashboard.statusVoided'),        'sd-fill-void'],
              ] as [keyof typeof invStats.counts, string, string][]).map(([key, label, cls]) => (
                <StatusBar
                  key={key}
                  label={label}
                  count={invStats.counts[key]}
                  total={invStats.total}
                  cls={cls}
                  amount={invStats.amounts[key]}
                  currency={currency}
                />
              ))}
            </div>
          </div>

          {/* Payment status rings */}
          <div className="ims-card">
            <div className="ims-card-header">
              <span className="ims-card-title"><CreditCard size={15} strokeWidth={2} style={{verticalAlign:'middle', marginInlineEnd:6}} />{t('salesDashboard.paymentStatus')}</span>
            </div>
            <div className="ims-card-body sd-payment-body">
              {invStats.total === 0 && !loading ? (
                <p className="sd-empty">{t('salesDashboard.noData')}</p>
              ) : (
                <div className="sd-payment-rings">
                  {([
                    ['Unpaid',        invStats.paymentCounts.Unpaid,        t('salesDashboard.payUnpaid'),        'sd-ring-unpaid'],
                    ['PartiallyPaid', invStats.paymentCounts.PartiallyPaid, t('salesDashboard.payPartiallyPaid'), 'sd-ring-partial'],
                    ['Paid',          invStats.paymentCounts.Paid,          t('salesDashboard.payPaid'),          'sd-ring-paid'],
                  ] as [string, number, string, string][]).map(([key, count, label, cls]) => {
                    const pct  = invStats.total > 0 ? Math.round((count / invStats.total) * 100) : 0;
                    const circ = 2 * Math.PI * 32;
                    const dash = (pct / 100) * circ;
                    return (
                      <div key={key} className="sd-ring-item">
                        <svg width="80" height="80" viewBox="0 0 80 80">
                          <circle cx="40" cy="40" r="32" fill="none" stroke="var(--sd-ring-track)" strokeWidth="10" />
                          <circle
                            cx="40" cy="40" r="32" fill="none"
                            strokeWidth="10"
                            strokeDasharray={`${dash} ${circ - dash}`}
                            strokeDashoffset={circ * 0.25}
                            strokeLinecap="round"
                            className={cls}
                          />
                          <text x="40" y="44" textAnchor="middle" className="sd-ring-pct">{pct}%</text>
                        </svg>
                        <div className="sd-ring-label">{label}</div>
                        <div className="sd-ring-count">{num(count)}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Recent invoices — full width ──────────────────────────────── */}
        <div className="ims-card">
          <div className="ims-card-header">
            <span className="ims-card-title"><Receipt size={15} strokeWidth={2} style={{verticalAlign:'middle', marginInlineEnd:6}} />{t('salesDashboard.recentInvoices')}</span>
            <button className="sd-link-btn" onClick={() => navigate('/sales/sales-invoices')}>
              {t('salesDashboard.viewAll')} →
            </button>
          </div>
          <div className="ims-card-body sd-no-pad">
            {invStats.recent.length === 0 && !loading ? (
              <p className="sd-empty">{t('salesDashboard.noInvoices')}</p>
            ) : (
              <table className="sd-table">
                <thead>
                  <tr>
                    <th>{t('salesDashboard.colInvoice')}</th>
                    <th>{t('salesDashboard.colCustomer')}</th>
                    <th>{t('salesDashboard.colStatus')}</th>
                    <th className="sd-right">{t('salesDashboard.colAmount')}</th>
                    <th className="sd-right">{t('salesDashboard.colBalance')}</th>
                  </tr>
                </thead>
                <tbody>
                  {invStats.recent.map(inv => (
                    <tr
                      key={inv.id}
                      className="sd-row-link"
                      onClick={() => navigate(`/sales/sales-invoices/${inv.id}`)}
                    >
                      <td><code className="sd-ref">{inv.invoiceNumber}</code></td>
                      <td className="sd-clamp">{inv.customerName}</td>
                      <td>
                        <span className={INVOICE_STATUS_CLS[inv.status] ?? 'sd-badge sd-badge-draft'}>
                          {t(`salesDashboard.status${inv.status}`, inv.status)}
                        </span>
                      </td>
                      <td className="sd-right">{currency(inv.totalAmount)}</td>
                      <td className={`sd-right ${inv.balanceDue > 0 ? 'sd-red' : 'sd-green'}`}>
                        {currency(inv.balanceDue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* ── Recent restaurant orders — a company on the restaurant module has
            no equivalent invoice list, so this stays a separate table rather
            than being forced into the invoice-shaped one above ──────────── */}
        {dashboardSummary && dashboardSummary.restaurantOrderCount > 0 && (
          <div className="ims-card">
            <div className="ims-card-header">
              <span className="ims-card-title"><UtensilsCrossed size={15} strokeWidth={2} style={{verticalAlign:'middle', marginInlineEnd:6}} />{t('salesDashboard.recentRestaurantOrders', 'Recent Restaurant Orders')}</span>
              <button className="sd-link-btn" onClick={() => navigate('/restaurant/orders')}>
                {t('salesDashboard.viewAll')} →
              </button>
            </div>
            <div className="ims-card-body sd-no-pad">
              <table className="sd-table">
                <thead>
                  <tr>
                    <th>{t('salesDashboard.colInvoice')}</th>
                    <th>{t('salesDashboard.colCustomer')}</th>
                    <th>{t('salesDashboard.colStatus')}</th>
                    <th className="sd-right">{t('salesDashboard.colAmount')}</th>
                    <th className="sd-right">{t('salesDashboard.colBalance')}</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardSummary.recentTransactions
                    .filter(x => x.source === 'RestaurantOrder')
                    .map(o => (
                      <tr
                        key={o.id}
                        className="sd-row-link"
                        onClick={() => navigate('/restaurant/orders')}
                      >
                        <td><code className="sd-ref">{o.reference}</code></td>
                        <td className="sd-clamp">{o.customerName ?? '—'}</td>
                        <td><span className="sd-badge sd-badge-draft">{o.status}</span></td>
                        <td className="sd-right">{currency(o.totalAmount)}</td>
                        <td className={`sd-right ${o.balanceDue > 0 ? 'sd-red' : 'sd-green'}`}>
                          {currency(o.balanceDue)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Quick actions ─────────────────────────────────────────────── */}
        <div className="sd-section-title">{t('salesDashboard.quickActions')}</div>
        <div className="sd-qa-grid">
          {([
            [<Receipt size={26} strokeWidth={1.5} />,       t('salesDashboard.qa_newInvoice'),   '/sales/sales-invoices/create'],
            [<AlertTriangle size={26} strokeWidth={1.5} />, t('salesDashboard.qa_overdue'),      '/sales/sales-invoices'],
            [<Users size={26} strokeWidth={1.5} />,         t('salesDashboard.qa_customers'),    '/customers'],
            [<BarChart2 size={26} strokeWidth={1.5} />,     t('salesDashboard.qa_allInvoices'),  '/sales/sales-invoices'],
            [<ShoppingBag size={26} strokeWidth={1.5} />,   t('salesDashboard.qa_salesItems'),   '/sales-items'],
          ] as [React.ReactNode, string, string][]).map(([icon, label, path], idx) => (
            <button key={`${path}-${idx}`} className="sd-qa-btn" onClick={() => navigate(path)}>
              <span className="sd-qa-icon">{icon}</span>
              <span className="sd-qa-label">{label}</span>
            </button>
          ))}
        </div>

      </div>
    </Layout>
  );
};

export default SalesDashboardPage;
